import React from 'react';
import { Popover } from 'react-bootstrap';
import { IconFileImport, IconMessageCircle, IconShoppingCart } from '@tabler/icons-react';
import { format, isValid, parseISO } from 'date-fns';

import OtaIcon from './OtaIcon';
import { BookingPayment } from '../../../../shared/types/booking/BookingPaymentType';
import { calculateNights, calculateNightsText } from '../../../../shared/utils/dateUtils';

/**
 * Tooltip RINGKAS saat hover event booking di Kalender Reservasi.
 *
 * Sengaja dibuat sekecil mungkin (meniru tooltip DPM di Monthly Setup): hanya
 * info yang paling sering dibutuhkan sekilas, tanpa aksi apa pun.
 *
 * PENTING — jangan tambahkan pemanggilan API di sini. Seluruh datanya diambil
 * dari `extendedProps` event yang SUDAH ada di memori (hasil
 * `mapBookingEventCalendar`), sehingga hover tidak memicu request sama sekali.
 * Detail lengkap + tombol aksi tetap lewat KLIK (popover `TooltipContentNew1`),
 * yang barulah memanggil `useShowBooking`.
 *
 * Dipakai lewat bentuk FUNGSI pada prop `overlay` OverlayTrigger:
 *   overlay={(props) => <BookingHoverTooltip {...props} ... />}
 * Bentuk fungsi (bukan element) membuat isi tooltip baru dibangun saat benar-
 * benar di-hover — penting karena kalender bisa berisi puluhan event sekaligus.
 */

export interface BookingHoverData {
    customerName?: string | null;
    customerSurname?: string | null;
    otaName?: string | null;
    checkinDate?: string | null;
    checkoutDate?: string | null;
    /** 'hotel_collect' | 'channel_collect' | '' | null */
    paymentType?: string | null;
    publicNote?: string | null;
    privateNote?: string | null;
    /** Booking hasil bulk-import di channel manager, bukan reservasi normal. */
    isImported?: boolean | null;
    /** Booking punya minimal 1 extra/service aktif (bukan yang sudah dibatalkan). */
    hasExtra?: boolean | null;
    /** Jumlah item extra AKTIF (predikat sama persis dengan hasExtra). */
    extraCount?: number | null;
}

/** Format tanggal pendek; mengembalikan null kalau tanggalnya tidak valid. */
const shortDate = (value?: string | null): string | null => {
    if (!value) return null;
    const parsed = parseISO(value);
    if (!isValid(parsed)) return null;
    return format(parsed, 'dd MMM');
};

/**
 * `calculateNights` MELEMPAR error untuk tanggal tidak valid — dibungkus di sini
 * supaya satu baris data rusak tidak menjatuhkan render kalender.
 */
const safeNightsText = (checkin?: string | null, checkout?: string | null): string | null => {
    if (!checkin || !checkout) return null;
    try {
        const nights = calculateNights(checkin, checkout);
        if (!nights || nights < 1) return null;
        return calculateNightsText(nights);
    } catch {
        return null;
    }
};

/**
 * Label payment type. `null`/`''` dipetakan ke "Not Set" (entri itu memang ada
 * di `BookingPayment`). Nilai tak dikenal ditampilkan apa adanya, bukan dipaksa
 * jadi "Not Set", supaya tidak menyembunyikan data yang sebenarnya ada.
 */
const paymentTypeLabel = (paymentType?: string | null): { label: string; isNotSet: boolean } => {
    const raw = paymentType ?? '';
    const known = BookingPayment.find((p) => p.value === raw);
    if (known) return { label: known.label, isNotSet: raw === '' };
    return { label: raw, isNotSet: false };
};

const hasText = (value?: string | null): boolean => typeof value === 'string' && value.trim() !== '';

/** Satu baris label/nilai. */
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="d-flex justify-content-between align-items-center gap-2">
        <span className="text-secondary">{label}</span>
        <span className="fw-bold text-end text-nowrap">{children}</span>
    </div>
);

interface BookingHoverTooltipProps extends React.ComponentPropsWithoutRef<typeof Popover> {
    bookingId: string;
    data: BookingHoverData;
}

/**
 * forwardRef wajib: OverlayTrigger menyuntikkan `ref` (plus style/placement/
 * arrowProps) untuk memosisikan popover. Prop suntikan itu diteruskan ke
 * `Popover`, `style` digabung supaya `maxWidth` milik kita tidak menimpa
 * koordinat posisi dari Popper.
 */
const BookingHoverTooltip = React.forwardRef<HTMLDivElement, BookingHoverTooltipProps>(
    ({ bookingId, data, style, ...overlayProps }, ref) => {
        const fullName = [data.customerName, data.customerSurname]
            .filter((part) => hasText(part))
            .join(' ')
            .trim();

        const checkin = shortDate(data.checkinDate);
        const checkout = shortDate(data.checkoutDate);
        const nightsText = safeNightsText(data.checkinDate, data.checkoutDate);
        const payment = paymentTypeLabel(data.paymentType);

        const showPublicNote = hasText(data.publicNote);
        const showPrivateNote = hasText(data.privateNote);
        const isImported = data.isImported === true;
        const hasExtra = data.hasExtra === true;
        const extraCount = typeof data.extraCount === 'number' && data.extraCount > 0 ? data.extraCount : null;

        return (
            <Popover
                ref={ref}
                {...overlayProps}
                id={`booking-hover-${bookingId}`}
                style={{ maxWidth: 240, ...style }}
            >
                <Popover.Header as="div" className="d-flex align-items-center gap-2 py-1 px-2">
                    <OtaIcon otaName={data.otaName ?? undefined} size={12} />
                    <span className="fw-bold text-truncate" style={{ fontSize: '12px' }}>
                        {fullName || 'No name'}
                    </span>
                </Popover.Header>

                <Popover.Body className="p-2" style={{ fontSize: '11.5px' }}>
                    {hasText(data.otaName) && <Row label="OTA">{data.otaName}</Row>}

                    {(checkin || checkout) && (
                        <Row label="Stay">
                            {checkin ?? '?'} → {checkout ?? '?'}
                        </Row>
                    )}

                    {nightsText && <Row label="Nights">{nightsText}</Row>}

                    <Row label="Payment">
                        <span className={payment.isNotSet ? 'text-secondary fst-italic fw-normal' : undefined}>
                            {payment.label || 'Not Set'}
                        </span>
                    </Row>

                    {/* "Extra" sengaja jadi baris sendiri (bukan ikon di baris marker
                        di bawah) supaya nilainya bisa menampilkan JUMLAH item extra,
                        bukan cuma penanda ada/tidak. `extraCount` fallback ke "Yes"
                        kalau backend lama cuma kirim hasExtra tanpa extraCount
                        (transisi deploy FE/BE tidak mengikat, lihat nullish di schema). */}
                    {hasExtra && (
                        <Row label="Extra">
                            <span className="d-inline-flex align-items-center gap-1 text-primary justify-content-end">
                                <IconShoppingCart size={13} />
                                {extraCount !== null ? `${extraCount} item${extraCount > 1 ? 's' : ''}` : 'Yes'}
                            </span>
                        </Row>
                    )}

                    {/* Label sengaja dipendekkan ("Public"/"Private"/dst — ikonnya sudah
                        cukup jelas) supaya tetap ringkas di lebar tooltip yang sempit.
                        "Extra" dipindah jadi baris sendiri di atas (lihat komentar di
                        atas) supaya baris marker ini paling banyak 3 penanda
                        (Imported/Public/Private) dan tidak gampang wrap ke baris kedua
                        di tooltip 240px. `flex-wrap` tetap dipertahankan sebagai jaring
                        pengaman kalau suatu saat ada penanda baru lagi. */}
                    {(showPublicNote || showPrivateNote || isImported) && (
                        <div className="d-flex align-items-center flex-wrap gap-2 mt-2 pt-2 border-top">
                            {isImported && (
                                <span className="d-inline-flex align-items-center gap-1 text-warning text-nowrap">
                                    <IconFileImport size={13} />
                                    Imported
                                </span>
                            )}
                            {showPublicNote && (
                                <span className="d-inline-flex align-items-center gap-1 text-success text-nowrap">
                                    <IconMessageCircle size={13} />
                                    Public
                                </span>
                            )}
                            {showPrivateNote && (
                                <span className="d-inline-flex align-items-center gap-1 text-danger text-nowrap">
                                    <IconMessageCircle size={13} />
                                    Private
                                </span>
                            )}
                        </div>
                    )}
                </Popover.Body>
            </Popover>
        );
    }
);

BookingHoverTooltip.displayName = 'BookingHoverTooltip';

export default BookingHoverTooltip;
